import {
  Box,
  Skeleton,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { ErrorBoundary } from '@sentry/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { useHistory } from 'react-router';

import { DataLake, Organization } from '@finley-technologies/finley-types';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { ExploDashboardContainer } from 'packages/explo/ui/ExploDashboardContainer';
import { ExploReportBuilderContainer } from 'packages/explo/ui/ExploReportBuilderContainer';
import { QuickSightContainer } from 'packages/quickSight/ui/QuickSightContainer';
import { StratsTemplate } from 'packages/strats/ui/StratsTemplate';

export const StratsRoute = withLDConsumer()(({ flags }) => {
  const toast = useToast();
  const history = useHistory();
  const { creditFacilityId } = useCreditFacilityContext();
  const { data: userData, loading: userLoading } = useGetUserQuery();

  if (userLoading) {
    return <Skeleton height="600px" data-testid="strats-route-loading" />;
  }

  if (!userData || !userData.user) {
    // TODO: currently defaulting similar app behavior when missing user data
    // Manage this potential error case
    return <Skeleton height="600px" data-testid="strats-route-missing-user" />;
  }

  if (!flags) {
    return <Skeleton height="600px" data-testid="strats-route-flag-unset" />;
  }

  if (flags.showStratsPage === 'hide' && !flags.showExploReportBuilder) {
    history.push('/');
    return <Skeleton height="600px" data-testid="strats-route-reroute" />;
  }

  const organizationId = userData.user.organizationId;

  // Use this variable for errors that bubble up to the component level route
  // This ensures we only toast the error once when there are multiple errors
  // We should use Chakra's toast.isActive, or a react hook in general, but the state re-renders when there are multiple errors in a given error boundary
  let routeLevelError = false;
  return (
    <Box className="main" width="100%">
      <ErrorBoundary
        onError={(err) => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            errorToastConfig.title = `Failed to load strats page ${err}`;
            routeLevelError = true;
            toast(errorToastConfig);
            history.push('/');
          }
        }}
      >
        <StratsTemplate organizationId={organizationId} header="Strats">
          <Tabs mb="3" variant="line">
            <TabList>
              {flags.showStratsPage !== 'hide' && <Tab>Dashboard</Tab>}
              {flags.showExploReportBuilder && <Tab>Report Builder</Tab>}
            </TabList>

            <TabPanels>
              {/*
               * TODO: at the moment, we're still in this intermediary state
               * where we're supporting both Quicksights & Explo, thus requiring
               * all these flags and respective states. Ideally we'll only support
               * one platform soon and can clean up below to not be so conditional
               */}
              {/* eslint-disable-next-line no-nested-ternary */}
              {flags.showStratsPage === 'quicksight' ? (
                <TabPanel>
                  <QuickSightContainer
                    containerId="qs-borrower-strats-dashboard"
                    embedType={DataLake.QuicksightEmbedType.Dashboard}
                    embedUrlQueryParams={{
                      organizationId,
                      organizationType: Organization.OrganizationType.Borrower,
                      dataType:
                        DataLake.QuickSightDataType.ReceivableResponseTape,
                      creditFacilityId,
                      latest: true,
                    }}
                    embedStyling={{
                      height: '704',
                      resizeHeightOnSizeChangedEvent: true,
                    }}
                  />
                </TabPanel>
              ) : flags.showStratsPage === 'explo' ? (
                <TabPanel>
                  <ExploDashboardContainer
                    organizationId={organizationId}
                    creditFacilityId={creditFacilityId}
                  />
                </TabPanel>
              ) : null}

              {flags.showExploReportBuilder && (
                <TabPanel>
                  <ExploReportBuilderContainer
                    organizationId={organizationId}
                    creditFacilityId={creditFacilityId}
                  />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </StratsTemplate>
      </ErrorBoundary>
    </Box>
  );
});
